// FCM client helper for the supplier portal.
//
// The backend (orderFanout.ts) sends new-order notifications to the `fcmToken`
// field on the supplier's users/{uid} doc. This file writes that token after
// sign-in if the browser has push permission granted.

import { getMessaging, getToken, deleteToken, onMessage, isSupported } from 'firebase/messaging'
import { doc, updateDoc } from 'firebase/firestore'
import app, { db } from './firebase'

const VAPID_KEY = import.meta.env.VITE_VAPID_KEY as string | undefined

function swRegistrationUrl(): string {
  const params = new URLSearchParams({
    apiKey:            import.meta.env.VITE_FIREBASE_API_KEY         ?? '',
    authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN     ?? '',
    projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID      ?? '',
    storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET  ?? '',
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '',
    appId:             import.meta.env.VITE_FIREBASE_APP_ID          ?? '',
  })
  return `/firebase-messaging-sw.js?${params.toString()}`
}

async function registerSW(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  if (!(await isSupported()))          return null
  try {
    return await navigator.serviceWorker.register(swRegistrationUrl())
  } catch (err) {
    console.warn('[FCM] service worker registration failed', err)
    return null
  }
}

async function fetchAndSaveToken(userId: string, registration: ServiceWorkerRegistration) {
  if (!VAPID_KEY) return
  try {
    const messaging = getMessaging(app)
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    })
    if (!token) return
    await updateDoc(doc(db, 'users', userId), { fcmToken: token })

    onMessage(messaging, (payload) => {
      const { title, body } = payload.notification || {}
      if (title && document.visibilityState !== 'visible') {
        try {
          new Notification(title, { body, icon: '/tarru-tree.svg' })
        } catch { /* ignore */ }
      }
    })
  } catch (err) {
    console.warn('[FCM] could not fetch or save token', err)
  }
}

// Called after sign-in. Silent — only registers if permission already granted.
export async function initializePush(userId: string) {
  if (!VAPID_KEY) {
    console.info('[FCM] VITE_VAPID_KEY not set — push notifications disabled')
    return
  }
  if (typeof Notification === 'undefined')         return
  if (Notification.permission !== 'granted')       return

  const reg = await registerSW()
  if (!reg) return
  await fetchAndSaveToken(userId, reg)
}

// User-initiated opt-out. Drops the local FCM token so the browser stops
// receiving messages. The Firestore-side cleanup (clearing fcmToken +
// setting pushOptOut) is the caller's responsibility.
export async function disablePush(): Promise<void> {
  try {
    if (!(await isSupported())) return
    const messaging = getMessaging(app)
    await deleteToken(messaging)
  } catch (err) {
    console.warn('[FCM] could not delete token', err)
  }
}

// User-initiated opt-in. Prompts for permission, registers if granted.
export async function requestPushPermission(userId: string): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') return 'denied'
  if (!VAPID_KEY) {
    console.warn('[FCM] VITE_VAPID_KEY not set — cannot enable push')
    return 'denied'
  }

  let permission = Notification.permission
  if (permission === 'default') {
    permission = await Notification.requestPermission()
  }
  if (permission !== 'granted') return permission

  const reg = await registerSW()
  if (!reg) return 'denied'
  await fetchAndSaveToken(userId, reg)
  return 'granted'
}
